import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { GhostClickBlocker, ModalCustomOverrides } from '@/app/components/search/components/Modal/OpenXModal/styled.ts';
import { sumClasses } from '@/app/components/search/utils/misc.ts';
import type { OpenxDialogProps } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { MODAL_THEME_VARIANTS } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import OverlayContext from '@/app/common/context/OverlayContext.ts';
import { useBrowserBackButton } from '@/app/components/hotel-detail/hooks/useBrowserBackButton';
import { useAddAttributes } from '@/app/common/hooks/useAddAttribute.ts';
import { useFocusTrap } from '@/app/common/hooks/useFocusTrap.ts';
import useIsModalMounted from '@/app/components/search/hooks/useIsModalMounted.ts';
import useScrollShadows from '@/app/common/hooks/useScrollShadows.ts';
import useSwipeToDismiss from '@/app/components/search/hooks/useSwipeToDismiss.ts';
import { addModalOpenClass, removeModalOpenClass } from '@/utils/domUtils.ts';
import {
  EVO_BODY_PADDING,
  EVO_BODY_PADDING_WIDE,
  EVO_BODY_PADDING_WIDE_WITHOUT_HEADER,
  EVO_BODY_PADDING_WITHOUT_HEADER,
} from '@/app/components/search/components/Modal/OpenXModal/utils.ts';

// MPH-299: the modal is portaled, so a click inside it still bubbles, through the React tree, into
// whatever rendered it (the result card, the badge that opened it), which must not see it. Only
// React's propagation is stopped, though. React dispatches a portal's events from its container,
// body here, so e.stopPropagation() would also stop the native click there, short of the document,
// where a design-system Select's menu listens for the press that closes it (MPH-1221).
const stopReactPropagation = (e: MouseEvent) => {
  e.isPropagationStopped = () => true;
};

const OpenXModalInner = ({
  bodyBackgroundColor,
  bodyOverflowY = 'auto',
  bodyPadding,
  bodyPaddingS,
  bodyPaddingXS,
  brand,
  children,
  className,
  disableAnimation,
  disableBrowserHistoryNavigation = false,
  disableTransition,
  footer,
  fullHeight,
  fullScreen = false,
  header,
  id,
  isMobile,
  isOpen,
  scrollShadows,
  onClose,
  onCloseByBrowserHistory,
  onOpenByBrowserHistory,
  onFinallyHistoryBack,
  stackedDialog,
  style,
  variant,
  size,
  ...props
}: OpenxDialogProps) => {
  const { shouldMount } = useIsModalMounted(isOpen);

  const shadowsEnabled = !!scrollShadows && shouldMount;
  const { probeRef, headerShadow, footerShadow } = useScrollShadows();

  // Ghost-click blocker: iOS Safari synthesises a click event up to ~300 ms after a
  // touch, but the Radix overlay leaves the DOM after its 150 ms exit animation. The
  // blocker (a transparent fixed div, z-index 1299) absorbs that delayed click so it
  // cannot reach content behind the modal. Active on all viewports (mobile + desktop).
  const wasOpenRef = useRef(isOpen);
  const [isBlockingClicks, setIsBlockingClicks] = useState(false);

  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      setIsBlockingClicks(false);
      return undefined;
    }
    if (!wasOpenRef.current) return undefined;
    setIsBlockingClicks(true);
    const timer = setTimeout(() => setIsBlockingClicks(false), 400);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useBrowserBackButton({
    condition: isOpen,
    modalId: id,
    onClose: onCloseByBrowserHistory,
    onOpen: onOpenByBrowserHistory,
    disabled: disableBrowserHistoryNavigation || !onCloseByBrowserHistory || !onOpenByBrowserHistory,
    onFinallyHistoryBack,
  });

  // Add before paint to prevent opening layout shift.
  useLayoutEffect(() => {
    if (shouldMount) {
      addModalOpenClass();
    }
  }, [shouldMount]);

  // Remove in useEffect so it batches with react-remove-scroll's data-scroll-locked cleanup —
  // both run in the same flush, preventing the one-frame window where the map loses its
  // right-offset while the scrollbar is still hidden (the closing flash). Cleanup-only: the
  // effect body must not remove on mount — instances mounted closed (nested modals inside an
  // open lateral modal's content) would decrement the count without a paired add.
  useEffect(() => (shouldMount ? removeModalOpenClass : undefined), [shouldMount]);

  // MPH-1221: the dialog's scroll lock (react-remove-scroll) cancels, from the document, every wheel
  // and touchmove outside the modal's own box, and a design-system Select's menu is portaled outside
  // it, to body. Stopped at body, those events never reach the lock and the menu scrolls; its own
  // overscroll-behavior: contain keeps the page behind from scrolling along. One listener per open
  // modal, so closing a stacked one leaves the modal under it with its own.
  useEffect(() => {
    if (!shouldMount) return undefined;
    const letSelectMenusScroll = (event: Event) => {
      if (event.target instanceof Element && event.target.closest('[data-lmn-select-menu]')) {
        event.stopPropagation();
      }
    };
    const { body } = document;
    body.addEventListener('wheel', letSelectMenusScroll, { passive: true });
    body.addEventListener('touchmove', letSelectMenusScroll, { passive: true });
    return () => {
      body.removeEventListener('wheel', letSelectMenusScroll);
      body.removeEventListener('touchmove', letSelectMenusScroll);
    };
  }, [shouldMount]);

  // Radix sets aria-modal on DialogPrimitive.Content (portal), but not on the wrapper element (#id).
  useAddAttributes({
    selector: `#${id}`,
    attributes: {
      'aria-modal': 'true',
    },
    condition: isOpen,
  });

  useFocusTrap({
    active: isOpen,
    getContainer: () => document.querySelector<HTMLElement>(`[data-openx-modal-paper="${id}"]`),
  });

  useSwipeToDismiss({
    enabled: isMobile && shouldMount && isOpen,
    onClose: () => onClose({}, 'escapeKeyDown'),
    modalId: id,
  });

  const overlayContextValue = useMemo(() => ({ headerShadow, footerShadow }), [headerShadow, footerShadow]);

  // MPH-1116: the Evo body is inset 24px on the desktop dialog and 16px once the modal becomes a
  // full-width sheet (Figma 979-40540 / 979-41409) — the same split the legacy defaults below make
  // with 3 / 2. Defaulting both arms to the 16px descriptor is what left a modal that passes no
  // padding of its own 8px narrower on desktop than its neighbours that spell the pair out.
  const evoBodyPadding = header ? EVO_BODY_PADDING_WIDE : EVO_BODY_PADDING_WIDE_WITHOUT_HEADER;
  const evoSheetBodyPadding = header ? EVO_BODY_PADDING : EVO_BODY_PADDING_WITHOUT_HEADER;

  if (!shouldMount && !isBlockingClicks) {
    return null;
  }

  return (
    <>
      {isBlockingClicks && (
        <GhostClickBlocker
          aria-hidden="true"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
      )}
      {shouldMount && (
        <OverlayContext.Provider value={overlayContextValue}>
          <ModalCustomOverrides
            onClick={stopReactPropagation}
            role="dialog"
            id={id}
            tabIndex={-1}
            data-openx-modal-paper={id}
            bodyBackgroundColor={
              variant === MODAL_THEME_VARIANTS.WHITE
                ? undefined
                : variant === MODAL_THEME_VARIANTS.GRAY
                  ? 'backgroundLayer01'
                  : bodyBackgroundColor
            }
            bodyOverflowY={bodyOverflowY}
            bodyPadding={bodyPadding ?? (scrollShadows ? evoBodyPadding : 3)}
            bodyPaddingS={bodyPaddingS ?? (scrollShadows ? evoSheetBodyPadding : 2)}
            bodyPaddingXS={bodyPaddingXS ?? (scrollShadows ? evoSheetBodyPadding : undefined)}
            header={header ? { ...header, element: header.element ?? undefined } : undefined}
            footer={footer}
            fullHeight={fullHeight}
            fullScreen={fullScreen}
            stackedDialog={stackedDialog}
            disableTransition={disableTransition}
            isOpen={isOpen}
            onClose={onClose}
            className={sumClasses(['openx-dialog', isMobile ? 'openx-dialog-mobile' : '', className || '', id])}
            brand={brand}
            style={style}
            size={size}
            $variant={variant}
            $disableAnimation={disableAnimation}
            $scrollShadows={scrollShadows}
            {...(props?.['aria-label'] ? { 'aria-label': props['aria-label'] } : {})}
          >
            {shadowsEnabled && <div ref={probeRef} style={{ display: 'none' }} aria-hidden="true" />}
            {children}
          </ModalCustomOverrides>
        </OverlayContext.Provider>
      )}
    </>
  );
};

export default OpenXModalInner;
